import React from 'react'
import gql from 'graphql-tag'
import { AggregationConfigs, AggregationOptions } from '../aggregations'
import { OrderFieldConfig } from '../../legacy-design-system/order'
import {
  allAggregationConfigs,
  vulnerabilityOrSinkGroupFragment,
  VulnerabilityOrSinkGroup
} from './shared'

import {
  getSeverityColumn,
  getApplicationColumn,
  getVulnerabilityColumn,
  getStatusColumn,
  getProtectStatusColumn,
  getDateDetectedColumn,
  getRowActionColumn
} from './grid-columns'

import {
  createVulnerabilitiesGrid,
  createVulnerabilitiesGridContext,
  createVulnerabilitiesGridContextProvider,
  BulkActionProps,
  filterAggregations
} from './grid'
import {
  getVulnerabilitiesRoute,
  getVulnerabilityDrilldownID,
  getVulnerabilityDrilldownRoute
} from '../route'
import { BulkActionList, FilteredActionList } from './actions/bulk-action-list'
import { getLeastPermissiveRole, getMostPermissiveRole } from '../roles'
import { createWrappedPagingNav } from '../vulnerability-dashboard'
import memoize from 'lodash.memoize'
import { SupportedLocales } from '../locale/locale'
import { Column } from '../../legacy-design-system/grid/index'
import { GQLVulnerability } from '../../graphql/schema/types/vulnerability'
import { GQLVulnerabilityOrSinkGroup } from '../../graphql/schema/types/vulnerability-sink-group'

type OrderField =
  | 'NAME'
  | 'LAST_DETECTED'
  | 'APPLICATION'
  | 'STATUS'
  | 'FIRST_DETECTED'
  | 'SEVERITY'

export const orderFieldConfig: OrderFieldConfig<OrderField> = {
  SEVERITY: {
    direction: 'DESC',
    message: { id: 'title_severityLevel' }
  },
  NAME: {
    direction: 'ASC',
    message: { id: 'title_name' },
    supportedLocales: [SupportedLocales.en]
  },
  APPLICATION: {
    direction: 'ASC',
    message: { id: 'title_application' }
  },
  LAST_DETECTED: {
    direction: 'DESC',
    message: { id: 'title_vulnerabilities_lastDetected_vulnerability' }
  },
  FIRST_DETECTED: {
    direction: 'DESC',
    message: { id: 'title_firstDetected_vulnerability' }
  },
  STATUS: {
    direction: 'ASC',
    message: { id: 'title_status' }
  }
}

type AggregationCategory =
  | 'servers'
  | 'applications'
  | 'tags'
  | 'severities'
  | 'types'
  | 'statuses'
  | 'substatus'
  | 'environments'
  | 'urls'
  | 'securityStandards'
  | 'technologies'
  | 'applicationTags'
  | 'applicationImportances'
  | 'languages'
  | 'sinks'
  | 'bugtrackerStatuses'
  | 'protectStatuses'

const {
  applications,
  servers,
  tags,
  statuses,
  substatus,
  securityStandards,
  severities,
  types,
  environments,
  urls,
  technologies,
  applicationTags,
  applicationImportances,
  languages,
  sinks,
  bugtrackerStatuses,
  protectStatuses
} = allAggregationConfigs

export const aggregationConfigs: AggregationConfigs<AggregationCategory> = {
  applications,
  servers,
  tags,
  statuses,
  substatus,
  securityStandards,
  severities,
  types,
  environments,
  urls,
  technologies,
  applicationTags,
  applicationImportances,
  languages,
  sinks,
  bugtrackerStatuses,
  protectStatuses
}

const initialAggregationSelections: AggregationOptions<AggregationCategory> = {
  applications: [],
  servers: [],
  tags: [],
  statuses: [],
  bugtrackerStatuses: [],
  substatus: [],
  securityStandards: [],
  severities: [],
  types: [],
  environments: [],
  urls: [],
  sinks: [],
  technologies: [],
  applicationTags: [],
  applicationImportances: [],
  languages: [],
  protectStatuses: []
}

export const pageQuery = gql`
  query OrganizationVulnerabilityGrid(
    $offset: Int!
    $limit: Int!
    $organizationID: ID!
    $orderBy: VulnerabilityOrder
    $filterText: String
    $quickFilter: String
    $dateFilter: VulnerabilityDateFilter
    $applications: [String!]
    $servers: [String!]
    $tags: [String!]
    $statuses: [String!]
    $substatus: [String!]
    $securityStandards: [String!]
    $severities: [String!]
    $types: [String!]
    $environments: [String!]
    $urls: [String!]
    $sinks: [String!]
    $groupedBy: VulnerabilityGroupedByFilter
    $technologies: [String!]
    $applicationTags: [String!]
    $applicationImportances: [String!]
    $applicationMetadata: [ApplicationMetadataFilter]
    $languages: [String!]
    $licensedOnly: Boolean!
    $bugtrackerStatuses: [String!]
    $protectStatuses: [String!]
  ) {
    vulnerabilitiesAndSinkGroups(
      offset: $offset
      limit: $limit
      organizationID: $organizationID
      orderBy: $orderBy
      filters: {
        filterText: $filterText
        quickFilter: $quickFilter
        dateFilter: $dateFilter
        servers: $servers
        applications: $applications
        tags: $tags
        statuses: $statuses
        substatus: $substatus
        securityStandards: $securityStandards
        severities: $severities
        types: $types
        environments: $environments
        urls: $urls
        sinks: $sinks
        technologies: $technologies
        applicationTags: $applicationTags
        applicationMetadata: $applicationMetadata
        applicationImportances: $applicationImportances
        languages: $languages
        licensedOnly: $licensedOnly
        groupedBy: $groupedBy
        bugtrackerStatuses: $bugtrackerStatuses
        protectStatuses: $protectStatuses
      }
    ) {
      items {
        ...VulnerabilityOrSinkGroup
      }
      total
      licensedVulnerabilityCount
    }
  }
  ${vulnerabilityOrSinkGroupFragment}
`

const quickFiltersQuery = gql`
  query VulnerabilityQuickFilters(
    $organizationID: ID!
    $filterText: String
    $dateFilter: VulnerabilityDateFilter
    $applications: [String!]
    $servers: [String!]
    $tags: [String!]
    $statuses: [String!]
    $substatus: [String!]
    $securityStandards: [String!]
    $severities: [String!]
    $types: [String!]
    $environments: [String!]
    $urls: [String!]
    $sinks: [String!]
    $technologies: [String!]
    $applicationTags: [String!]
    $applicationMetadata: [ApplicationMetadataFilter]
    $applicationImportances: [String!]
    $languages: [String!]
    $licensedOnly: Boolean!
    $bugtrackerStatuses: [String!]
    $protectStatuses: [String!]
  ) {
    vulnerabilityQuickFilters(
      organizationID: $organizationID
      filters: {
        filterText: $filterText
        dateFilter: $dateFilter
        servers: $servers
        applications: $applications
        tags: $tags
        statuses: $statuses
        substatus: $substatus
        securityStandards: $securityStandards
        severities: $severities
        types: $types
        environments: $environments
        urls: $urls
        sinks: $sinks
        technologies: $technologies
        applicationTags: $applicationTags
        applicationMetadata: $applicationMetadata
        applicationImportances: $applicationImportances
        languages: $languages
        licensedOnly: $licensedOnly
        bugtrackerStatuses: $bugtrackerStatuses
        protectStatuses: $protectStatuses
      }
    ) {
      id
      name
      count
    }
  }
`

const getAggregationQuery = (name: string, queryFragment: string) => gql`
query OrganizationVulnerability${name}Aggregations(
  $organizationID: ID!
  $filterText: String
  $quickFilter: String
  $dateFilter: VulnerabilityDateFilter
  $applications: [String!]
  $servers: [String!]
  $tags: [String!]
  $statuses: [String!]
  $substatus: [String!]
  $securityStandards: [String!]
  $severities: [String!]
  $types: [String!]
  $environments: [String!]
  $urls: [String!]
  $sinks: [String!]
  $technologies: [String!]
  $applicationTags: [String!]
  $applicationMetadata: [ApplicationMetadataFilter]
  $applicationImportances: [String!]
  $languages: [String!]
  $licensedOnly: Boolean!
  $bugtrackerStatuses: [String!]
  $protectStatuses: [String!]
) {
  vulnerabilityAggregations(
    organizationID: $organizationID
    filters: {
      filterText: $filterText
      quickFilter: $quickFilter
      dateFilter: $dateFilter
      servers: $servers
      applications: $applications
      tags: $tags
      statuses: $statuses
      substatus: $substatus
      securityStandards: $securityStandards
      severities: $severities
      types: $types
      environments: $environments
      urls: $urls
      sinks: $sinks
      technologies: $technologies
      applicationTags: $applicationTags
      applicationMetadata: $applicationMetadata
      applicationImportances: $applicationImportances
      languages: $languages
      licensedOnly: $licensedOnly
      bugtrackerStatuses: $bugtrackerStatuses
      protectStatuses: $protectStatuses
    }
  ) {
    ${queryFragment}
  }
}
`

const getCountKey = (name) => `${name} {
  count
  key
}`
const getCountKeyIdLabel = (name) => `${name} {
  count
  key {
    id
    label
  }
}`
const getCountKeyIdName = (name) => `${name} {
  count
  key {
    id
    name
  }
}`
const aggregationsStrings = [
  {
    name: 'Server',
    query: getCountKeyIdName('servers')
  },
  {
    name: 'Application',
    query: getCountKeyIdName('applications')
  },
  {
    name: 'Tags',
    query: getCountKey('tags')
  },
  {
    name: 'Status',
    query: `statuses {
      count
      key {
        id
        label
        subfiltersId
        subfilters {
          count
          keycode
          label
        }
      }
    }`
  },
  {
    name: 'BugtrackerStatus',
    query: getCountKeyIdLabel('bugtrackerStatuses')
  },
  {
    name: 'SecurityStandard',
    query: getCountKeyIdLabel('securityStandards')
  },
  {
    name: 'Severity',
    query: getCountKeyIdLabel('severities')
  },
  {
    name: 'Type',
    query: getCountKeyIdLabel('types')
  },
  {
    name: 'Environment',
    query: getCountKeyIdLabel('environments')
  },
  {
    name: 'Url',
    query: getCountKeyIdLabel('urls')
  },
  {
    name: 'Technologies',
    query: getCountKeyIdLabel('technologies')
  },
  {
    name: 'ApplicationTags',
    query: getCountKey('applicationTags')
  },
  {
    name: 'ApplicationMetadata',
    query: `applicationMetadata {
      id
      fieldType
      label
      buckets {
        count
        key {
          id
          value
          subfieldValues {
            fieldType
            value
          }
        }
      }
    }`
  },
  {
    name: 'ApplicationImportances',
    query: getCountKeyIdLabel('applicationImportances')
  },

  {
    name: 'languages',
    query: getCountKeyIdLabel('languages')
  },
  {
    name: 'Sink',
    query: getCountKeyIdLabel('sinks')
  },
  {
    name: 'protectStatuses',
    query: getCountKeyIdLabel('protectStatuses')
  }
]
export const aggregationsQueries = aggregationsStrings.map(({ name, query }) =>
  getAggregationQuery(name, query)
)

export const OrganizationVulnerabiltiesGridContext =
  createVulnerabilitiesGridContext<OrderField, AggregationCategory>({
    initialAggregationSelections,
    initialOrder: 'SEVERITY',
    aggregationConfigs,
    orderFieldConfig
  })

export const getGridPath = ({
  organizationID,
  routeParameters: _routeParameters
}) => getVulnerabilitiesRoute(organizationID)

export const getDrilldownPath = ({ organizationID, id }) =>
  getVulnerabilityDrilldownRoute(organizationID, id)

export const OrganizationVulnerabiltiesPagingNav = createWrappedPagingNav({
  // @ts-ignore todo TS-3.8.3 - TS-8508
  GridContext: OrganizationVulnerabiltiesGridContext,
  getCurrentIdFromRoute: getVulnerabilityDrilldownID,
  getGridPath,
  getDrilldownPath
})

const VulnerabilitiesGridMain = createVulnerabilitiesGrid(
  OrganizationVulnerabiltiesGridContext
)

export const getQueries = memoize(() => ({
  pageQuery,
  aggregationsQueries,
  quickFiltersQuery
}))

export const OrganizationVulnerabilitiesGridContextProvider =
  createVulnerabilitiesGridContextProvider(
    // @ts-ignore todo TS-3.8.3 - TS-8508
    OrganizationVulnerabiltiesGridContext,
    aggregationConfigs,
    'vulns',
    'SEVERITY',
    orderFieldConfig,
    getQueries,
    (routeParameters) =>
      routeParameters.length >= 2 && routeParameters[1].indexOf('vulns') > -1
  )

export function OrganizationVulnerabilitiesGrid() {
  return (
    <VulnerabilitiesGridMain
      isDrilldown={false}
      renderBulkActions={renderVulnerabilityOrSinkGroupBulkActions}
      columns={({
        aggregations,
        metadataSelections,
        onMetadataOptionsToggle,
        onClearMetadataSelections,
        organizationID,
        organizationRole,
        hasLicenses,
        dateRange,
        dateRangeOption,
        onSetCustomDateRange,
        onSetDateRangeOption,
        onClearDateRange,
        dateField,
        onSetDateField,
        features,
        refresh,
        refetchQueries,
        trackJob,
        restrictedEditRole,
        protectAssessEnv,
        isProtectColumnEnabled,
        isProtectEnabled
      }) =>
        getAllColumns({
          aggregations,
          metadataSelections,
          onMetadataOptionsToggle,
          onClearMetadataSelections,
          organizationID,
          organizationRole,
          hasLicenses,
          dateRange,
          dateRangeOption,
          onSetCustomDateRange,
          onSetDateRangeOption,
          onClearDateRange,
          dateField,
          onSetDateField,
          features,
          refresh,
          refetchQueries,
          trackJob,
          restrictedEditRole,
          protectAssessEnv,
          isProtectColumnEnabled,
          isProtectEnabled
        })
      }
      showLicensedOnlyCheckbox
    />
  )
}

export function renderVulnerabilityOrSinkGroupBulkActions({
  rowSelectionProps,
  organizationID,
  organizationRole,
  refetchQueries,
  refresh,
  isFreemiumOrganization,
  trackJob,
  restrictedEditRole
}: BulkActionProps<GQLVulnerabilityOrSinkGroup, AggregationCategory>) {
  const {
    selectedCount,
    selectedItems,
    deselectedItems,
    filters,
    additionalFilters
  } = rowSelectionProps

  const deSelectedVulns = deselectedItems
    .filter((vulnOrSinkGroup) => !!vulnOrSinkGroup.vulnerability)
    .map((withVuln) => withVuln.vulnerability) as GQLVulnerability[]
  const selectedVulns = selectedItems
    .filter((vulnOrSinkGroup) => vulnOrSinkGroup.vulnerability)
    .map((withVuln) => withVuln.vulnerability) as GQLVulnerability[]

  const mostPermissiveApplicationRole = getMostPermissiveRole(
    selectedVulns
      .concat(deSelectedVulns)
      .map((vulnerability) => vulnerability.application.role)
  )

  const leastPermissiveApplicationRoleOfSelected = getLeastPermissiveRole(
    selectedVulns.map((vulnerability) => vulnerability.application.role)
  )

  return rowSelectionProps.invertedSelection &&
    filters.quickFilter &&
    filters.aggregations ? (
    <FilteredActionList
      organizationID={organizationID}
      refetchQueries={refetchQueries}
      refresh={refresh}
      selectedCount={selectedCount}
      vulnerabilities={selectedVulns}
      excludedVulnerabilities={deSelectedVulns}
      filters={{
        aggregations: filterAggregations(filters.aggregations),
        ...filters,
        quickFilter: filters.quickFilter
      }}
      leastPermissiveApplicationRoleOfSelected={
        leastPermissiveApplicationRoleOfSelected
      }
      additionalFilters={additionalFilters}
      isFreemiumOrganization={isFreemiumOrganization}
      trackJob={trackJob}
      validateIfVulnerabilitiesAreFromSameApplication={true}
    />
  ) : (
    <BulkActionList
      organizationID={organizationID}
      refetchQueries={refetchQueries}
      refresh={refresh}
      organizationRole={organizationRole}
      leastPermissiveApplicationRoleOfSelected={
        leastPermissiveApplicationRoleOfSelected
      }
      mostPermissiveApplicationRole={mostPermissiveApplicationRole}
      vulnerabilities={selectedVulns}
      isFreemiumOrganization={isFreemiumOrganization}
      trackJob={trackJob}
      validateIfVulnerabilitiesAreFromSameApplication={true}
      restrictedEditRole={restrictedEditRole}
    />
  )
}

function getAllColumns({
  aggregations,
  metadataSelections,
  onMetadataOptionsToggle,
  onClearMetadataSelections,
  organizationID,
  organizationRole,
  hasLicenses,
  dateRange,
  dateRangeOption,
  onSetCustomDateRange,
  onSetDateRangeOption,
  onClearDateRange,
  dateField,
  onSetDateField,
  features,
  refresh,
  refetchQueries,
  trackJob,
  restrictedEditRole,
  protectAssessEnv,
  isProtectColumnEnabled,
  isProtectEnabled
}): Array<Column<VulnerabilityOrSinkGroup, AggregationCategory>> {
  return [
    getSeverityColumn<AggregationCategory>({
      aggregations: ['severities'],
      organizationID,
      refetchQueries,
      refresh
    }),
    getVulnerabilityColumn<AggregationCategory>({
      aggregations: [
        'tags',
        'bugtrackerStatuses',
        'types',
        'servers',
        'environments',
        'sinks',
        'urls',
        'securityStandards'
      ],
      organizationID,
      organizationRole,
      dateField,
      hasLicenses,
      features,
      refresh,
      refetchQueries,
      getDrilldownPath: (id) =>
        getVulnerabilityDrilldownRoute(organizationID, id)
    }),
    getProtectStatusColumn<AggregationCategory>({
      aggregations: ['protectStatuses' as AggregationCategory],
      protectAssessEnv,
      isProtectColumnEnabled,
      isProtectEnabled
    }),
    getApplicationColumn<AggregationCategory>({
      organizationID,
      organizationRole,
      hasLicenses,
      features,
      aggregations: [
        'applications',
        'applicationTags',
        'applicationImportances',
        'languages',
        'technologies'
      ],
      customAggregations: aggregations?.applicationMetadata,
      metadataSelections,
      onMetadataOptionsToggle,
      onClearMetadataSelections
    }),
    getDateDetectedColumn<AggregationCategory>({
      dateRange,
      dateRangeOption,
      onSetCustomDateRange,
      onSetDateRangeOption,
      onClearDateRange,
      dateField,
      onSetDateField
    }),
    getStatusColumn<AggregationCategory>({
      organizationID,
      refetchQueries,
      refresh,
      aggregations: ['statuses', 'substatus']
    }),
    getRowActionColumn({
      organizationID,
      organizationRole,
      refresh,
      refetchQueries,
      trackJob,
      restrictedEditRole
    })
  ]
}
